import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, FileText, CreditCard, ShieldCheck, FileWarning, DollarSign,
  Building2, Sparkles, Loader2, RefreshCw, ArrowRight, MessageSquare, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { friendlyError } from "@/lib/errors";

/**
 * /admin — Admin Dashboard homepage.
 *
 * Landing hub for the site owner. Shows a live snapshot of the platform
 * (users, cases, revenue, pending directory apps, open RFE/NOIDs) and
 * links to every specialized admin sub-page.
 *
 * Accessible only to users with role='admin' — enforced by both the
 * backend `require_admin` dependency on /admin/stats and a client-side
 * redirect for non-admins.
 */

function StatCard({ icon: Icon, label, value, sublabel, testId }) {
  return (
    <div className="card-surface p-5" data-testid={testId}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>
            {label}
          </div>
          <div className="font-display font-extrabold text-3xl mt-1" style={{ color: "var(--text-primary)" }}>
            {value}
          </div>
          {sublabel && (
            <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
              {sublabel}
            </div>
          )}
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}
        >
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

function LinkTile({ icon: Icon, title, subtitle, to, testId, accent = false }) {
  return (
    <Link
      to={to}
      className="card-surface p-5 group hover:shadow-lg transition-shadow"
      style={accent ? { borderLeft: "3px solid var(--accent-copper)" } : {}}
      data-testid={testId}
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
             style={{ background: "var(--bg-cream)", color: "var(--accent-copper)" }}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-base" style={{ color: "var(--text-primary)" }}>
              {title}
            </h3>
            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: "var(--accent-copper)" }} />
          </div>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            {subtitle}
          </p>
        </div>
      </div>
    </Link>
  );
}

function money(cents) {
  const n = (cents || 0) / 100;
  return n.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });
}

export default function AdminHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/stats");
      setStats(data);
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't load the admin stats."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load();   }, []);

  if (user && user.role !== "admin") {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto py-16 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--text-secondary)" }} />
          <h1 className="font-display font-bold text-2xl mb-2" style={{ color: "var(--text-primary)" }}>
            Admins only
          </h1>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
            This area is restricted to platform administrators.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto py-8 px-4" data-testid="admin-home">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--accent-copper)" }}>
              Admin
            </div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl" style={{ color: "var(--text-primary)" }}>
              Dashboard
            </h1>
            <p className="text-sm mt-2 max-w-xl" style={{ color: "var(--text-secondary)" }}>
              Live snapshot of DS-260USA.com. Notifications for new signups,
              payments, and case filings arrive at{" "}
              <strong style={{ color: "var(--text-primary)" }}>your admin email inbox</strong>.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="btn-ghost text-xs flex items-center gap-1.5"
            style={{ border: "1px solid var(--border-stone)" }}
            data-testid="admin-refresh-btn"
          >
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>

        {/* Stat cards */}
        {stats && (
          <>
            <MilestoneBanner stats={stats} />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            <StatCard
              icon={Users}
              label="Total users"
              value={stats.users.total.toLocaleString()}
              sublabel={`${stats.users.signups_24h} in last 24h · ${stats.users.signups_7d} in last 7d · ${stats.users.attorneys} attorneys`}
              testId="admin-stat-users"
            />
            <StatCard
              icon={FileText}
              label="Cases"
              value={stats.cases.total.toLocaleString()}
              sublabel={`${stats.cases.filed} filed · ${stats.cases.created_24h} new in 24h`}
              testId="admin-stat-cases"
            />
            <StatCard
              icon={DollarSign}
              label="Revenue (all-time)"
              value={money(stats.payments.revenue_cents)}
              sublabel={`${stats.payments.count} paid transactions`}
              testId="admin-stat-revenue"
            />
            <StatCard
              icon={Building2}
              label="Directory applications"
              value={stats.directory.pending}
              sublabel={`pending review · ${stats.directory.approved} approved`}
              testId="admin-stat-directory"
            />
            <StatCard
              icon={FileWarning}
              label="Open RFE / NOIDs"
              value={stats.notices.open_rfe_noid}
              sublabel={`${stats.notices.created_24h} notices uploaded in 24h`}
              testId="admin-stat-notices"
            />
            <StatCard
              icon={Sparkles}
              label="Uptime"
              value="Live"
              sublabel={`Snapshot as of ${new Date(stats.generated_at).toLocaleString()}`}
              testId="admin-stat-uptime"
            />
            </div>
          </>
        )}

        {/* Quick actions */}
        <h2 className="font-display font-bold text-lg mb-4" style={{ color: "var(--text-primary)" }}>
          Quick actions
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
          <LinkTile
            icon={Building2}
            title="Directory applications"
            subtitle={
              stats && stats.directory.pending > 0
                ? `Review ${stats.directory.pending} pending attorney / paralegal application${stats.directory.pending === 1 ? "" : "s"}`
                : "Approve or reject applications from attorneys and paralegals"
            }
            to="/admin/directory"
            accent={stats && stats.directory.pending > 0}
            testId="admin-link-directory"
          />
          <LinkTile
            icon={CreditCard}
            title="Pricing"
            subtitle="Set the DS-260USA platform fee (per DS-260 applicant / per additional family member)"
            to="/admin/pricing"
            testId="admin-link-pricing"
          />
          <LinkTile
            icon={Users}
            title="Free-access testers"
            subtitle="Grant or revoke complimentary access for test users"
            to="/admin/testers"
            testId="admin-link-testers"
          />
          <LinkTile
            icon={MessageSquare}
            title="Testimonials"
            subtitle="Curate homepage testimonials shown to prospects"
            to="/admin/testimonials"
            testId="admin-link-testimonials"
          />
          <LinkTile
            icon={Megaphone}
            title="Affiliates"
            subtitle="Approve external promoters, track conversions, and mark payouts as paid"
            to="/admin/affiliates"
            testId="admin-link-affiliates"
          />
          <LinkTile
            icon={DollarSign}
            title="Friend & Family Referrals"
            subtitle="Manage FF-* UIDs (Option A gifts, Option B split-reward payouts)"
            to="/admin/friends-and-family"
            testId="admin-link-friends-family"
          />
          <LinkTile
            icon={ShieldCheck}
            title="Attorney invitations"
            subtitle="Invite specific attorneys to review a client's case"
            to="/attorney"
            testId="admin-link-attorney"
          />
        </div>

        {/* Notification cheat-sheet */}
        <div className="card-surface p-5 mt-8" data-testid="admin-notif-cheatsheet">
          <h3 className="font-display font-bold text-base mb-3" style={{ color: "var(--text-primary)" }}>
            Notifications you receive by email
          </h3>
          <ul className="text-sm space-y-2" style={{ color: "var(--text-primary)" }}>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--accent-copper)" }} />
              <div>
                <strong>New user signup</strong> — every client and attorney registration.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--accent-copper)" }} />
              <div>
                <strong>Directory applications</strong> — when a new attorney or paralegal applies.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--accent-copper)" }} />
              <div>
                <strong>Payment success</strong> — every completed checkout with the amount and case ID.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--accent-copper)" }} />
              <div>
                <strong>Case marked complete</strong> — when a user confirms their family's DS-260s were submitted at ceac.state.gov.
              </div>
            </li>
            <li className="flex items-start gap-2">
              <ArrowRight className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "var(--accent-copper)" }} />
              <div>
                <strong>NVC follow-up received</strong> — when a user uploads an NVC or consulate letter requesting more documents.
              </div>
            </li>
          </ul>
          <p className="text-xs mt-4" style={{ color: "var(--text-secondary)" }}>
            Reply directly to any admin email to reach the user (their address is set as the reply-to).
          </p>
        </div>

        {/* Backups card */}
        <BackupsCard />
        <AcceptanceRemindersCard />
      </div>
    </AppShell>
  );
}

function AcceptanceRemindersCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const runNow = async () => {
    setRunning(true); setErr(""); setResult(null);
    try {
      const { data } = await api.post("/admin/acceptance-reminders/run-now");
      setResult(data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setRunning(false); }
  };
  return (
    <div className="card-surface p-5 mt-4" data-testid="admin-accept-reminders-card">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg" style={{ background: "var(--ai-bg)" }}>
          <RefreshCw className="w-5 h-5" style={{ color: "var(--ai-text)" }} />
        </div>
        <div className="flex-1">
          <div className="font-display font-bold text-base mb-1">
            Acceptance reminders
          </div>
          <div className="text-xs mb-3" style={{ color: "var(--text-secondary)" }}>
            Emails clients who paid but haven't clicked "Accept Case as
            Complete and Ready for Filing". First nudge at 3 days after payment,
            final nudge at 10 days. Runs automatically every 6 hours. Click below
            to run right now.
          </div>
          <button className="btn-primary text-sm" onClick={runNow}
                  disabled={running}
                  data-testid="admin-accept-reminders-run">
            {running ? "Running…" : "Run reminder pass now"}
          </button>
          {result && (
            <div className="mt-2 text-xs p-2 rounded"
                 style={{ background: "#D1FAE5", color: "#065F46" }}
                 data-testid="admin-accept-reminders-result">
              Sent: {result.stage_1_sent} first-nudge + {result.stage_2_sent} final-nudge emails.
            </div>
          )}
          {err && (
            <div className="mt-2 text-xs p-2 rounded"
                 style={{ background: "#FEF2F2", color: "#991B1B" }}>
              {err}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function BackupsCard() {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/backups");
      setState(data);
    } catch (err) {
      // Non-fatal: backups may not be enabled (BACKUPS_ENABLED=false), in
      // which case the endpoint returns 404. Log so we can distinguish that
      // from real errors during debugging, but don't surface to the UI.
      if (err?.response?.status !== 404) {
        console.warn("[AdminHome] /admin/backups fetch failed:", err);
      }
    }
  };
  useEffect(() => { load(); }, []);
  const trigger = async () => {
    setBusy(true);
    try {
      await api.post("/admin/backups/run-now");
      toast.success("Backup complete.");
      await load();
    } catch (err) {
      toast.error(friendlyError(err, "Backup failed."));
    } finally {
      setBusy(false);
    }
  };
  if (!state) return null;
  return (
    <div className="card-surface p-5 mt-4" data-testid="admin-backups-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display font-bold text-base" style={{ color: "var(--text-primary)" }}>
            Automated backups
          </h3>
          <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
            {state.enabled
              ? `Running every ${state.interval_hours}h · keeping the most recent ${state.retention_count} · stored at ${state.backup_dir}`
              : "Backups are disabled. Set BACKUPS_ENABLED=true and restart to enable."}
          </p>
        </div>
        <button
          onClick={trigger}
          disabled={busy || !state.enabled}
          className="btn-ghost text-xs flex items-center gap-1.5"
          style={{ border: "1px solid var(--border-stone)" }}
          data-testid="admin-backup-run-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Back up now
        </button>
      </div>
      {state.archives.length === 0 ? (
        <div className="text-xs" style={{ color: "var(--text-secondary)" }}>No archives yet.</div>
      ) : (
        <ul className="text-xs space-y-1 mt-3" style={{ color: "var(--text-primary)" }} data-testid="admin-backup-list">
          {state.archives.slice(0, 5).map((a) => (
            <li key={a.filename} className="flex items-baseline justify-between gap-2">
              <span className="font-mono">{a.filename}</span>
              <span className="opacity-60">
                {(a.size_bytes / (1024 * 1024)).toFixed(1)} MB · {new Date(a.created_at).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/**
 * Milestone banner — surfaces reminders on the dashboard once you cross
 * a growth threshold. Each milestone is dismissible per-browser via
 * localStorage so it doesn't nag once acknowledged.
 *
 * Add more milestones by appending entries to MILESTONES below.
 */
const MILESTONES = [
  {
    id: "revenue-charts-500-paid",
    threshold: (s) => (s?.payments?.count || 0) >= 500,
    label: "You've crossed 500 paid cases 🎉",
    body: "Time to build the revenue-by-month line chart and the signups → paying conversion funnel on this dashboard. Reply to this reminder (or paste it into a new chat) and I'll build it.",
  },
  {
    id: "signup-digest-1000",
    threshold: (s) => (s?.users?.total || 0) >= 1000,
    label: "1,000 users signed up ✉️",
    body: "You asked to revisit signup-notification volume at 1,000 users. Consider switching per-signup emails to a daily digest, or move to dashboard-only visibility.",
  },
  {
    id: "attorney-review-1000",
    threshold: (s) => (s?.cases?.total || 0) >= 1000,
    label: "1,000 cases opened ⚖️",
    body: "You asked to revisit the attorney-application review workflow at 1,000 cases. Consider adding a small paid team or auto-approving trusted state bar numbers.",
  },
  {
    id: "backup-offbox-1000-paid",
    threshold: (s) => (s?.payments?.count || 0) >= 1000,
    label: "1,000 paid customers — move backups off-box 💾",
    body: "Automated backups currently write to disk at /app/backups. At this scale, switch to S3/GCS with lifecycle rules so a K8s node loss doesn't nuke the archive history.",
  },
];

function MilestoneBanner({ stats }) {
  const [dismissed, setDismissed] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("admin_milestones_dismissed") || "[]");
    } catch { return []; }
  });

  const active = MILESTONES.filter(
    (m) => m.threshold(stats) && !dismissed.includes(m.id),
  );
  if (active.length === 0) return null;

  const dismiss = (id) => {
    const next = [...dismissed, id];
    setDismissed(next);
    localStorage.setItem("admin_milestones_dismissed", JSON.stringify(next));
  };

  return (
    <div className="mb-6 space-y-3" data-testid="admin-milestone-banner">
      {active.map((m) => (
        <div
          key={m.id}
          className="card-surface p-4 flex items-start gap-3"
          style={{
            background: "var(--bg-cream)",
            borderLeft: "3px solid var(--accent-copper)",
          }}
          data-testid={`admin-milestone-${m.id}`}
        >
          <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0"
                    style={{ color: "var(--accent-copper)" }} />
          <div className="flex-1">
            <div className="font-display font-bold text-sm mb-1"
                 style={{ color: "var(--text-primary)" }}>
              {m.label}
            </div>
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              {m.body}
            </p>
          </div>
          <button
            onClick={() => dismiss(m.id)}
            className="text-xs underline"
            style={{ color: "var(--text-secondary)" }}
            data-testid={`admin-milestone-dismiss-${m.id}`}
          >
            Dismiss
          </button>
        </div>
      ))}
    </div>
  );
}

